import { useState, useCallback } from 'react';
import axios from 'axios';
import logger from '../utils/logger';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

/**
 * Generic base hook for content generation features (synopsis, keywords, etc.)
 * Handles YouTube video info extraction, content generation, and prompt management
 * 
 * @param {string} featureType - The feature type (e.g., 'synopsis', 'keyword')
 * @param {string} resultField - The field name in API response (e.g., 'synopsis', 'keywords')
 */
export const useContentGeneration = (featureType, resultField) => {
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [youtubeVideoInfo, setYoutubeVideoInfo] = useState(null);
  const [extractingVideoInfo, setExtractingVideoInfo] = useState(false);
  const [generatedContent, setGeneratedContent] = useState('');
  const [currentProjectId, setCurrentProjectId] = useState(null);
  const [generatingContent, setGeneratingContent] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [promptVersion, setPromptVersion] = useState(1);
  const [promptTotalVersions, setPromptTotalVersions] = useState(1);
  const [selectedPromptId, setSelectedPromptId] = useState(null);
  const [selectedPromptVersionForUse, setSelectedPromptVersionForUse] = useState(null);
  const [isEditingPrompt, setIsEditingPrompt] = useState(false);
  const [editedPrompt, setEditedPrompt] = useState('');
  const [promptLoading, setPromptLoading] = useState(false);

  // Extract YouTube video information
  const extractYoutubeVideoInfo = useCallback(async (url) => {
    if (!url || !url.trim()) {
      setYoutubeVideoInfo(null);
      setGeneratedContent('');
      return;
    }

    // Validate YouTube URL
    if (!url.includes('youtube.com') && !url.includes('youtu.be')) {
      setYoutubeVideoInfo(null);
      setGeneratedContent('');
      return;
    }

    // Try client-side extraction first (safer - cookies stay in browser)
    setExtractingVideoInfo(true);
    
    try {
      const { extractYouTubeInfoClientSide } = await import('../utils/youtubeClientExtractor');
      const clientResult = await extractYouTubeInfoClientSide(url.trim());
      
      if (clientResult.success) {
        logger.log('✅ YouTube info extracted client-side (cookies never sent to server)');
        setYoutubeVideoInfo({
          title: clientResult.data.title,
          thumbnail: clientResult.data.thumbnail,
          description: clientResult.data.description,
          source: clientResult.source,
          clientSide: true // Flag to indicate this was extracted client-side (no cookies uploaded)
        });
        setGeneratedContent('');
        setExtractingVideoInfo(false);
        return; // Success - no need to call backend
      } else {
        logger.log('ℹ️ Client-side extraction failed, falling back to backend:', clientResult.error);
        // Continue to backend extraction as fallback
      }
    } catch (error) {
      logger.log('⚠️ Client-side extraction error (non-critical), using backend:', error.message);
      // Continue to backend extraction as fallback
    }
    
    try {
      const formData = new FormData();
      formData.append('youtube_url', url.trim());
      
      const response = await axios.post(`${API_BASE}/${featureType}/youtube/info`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 30000, // 30 seconds timeout
      });

      // Handle transcript - it can be a dict (with text, language, is_generated) or just text
      let transcriptData = null;
      if (response.data.transcript) {
        if (typeof response.data.transcript === 'object' && response.data.transcript.text) {
          // New format: dict with text, language, is_generated
          transcriptData = response.data.transcript;
        } else if (typeof response.data.transcript === 'string') {
          // Old format: just text string
          transcriptData = {
            text: response.data.transcript,
            language: 'en',
            is_generated: false
          };
        }
      }
      
      setYoutubeVideoInfo({
        title: response.data.title,
        transcript: transcriptData, // Add transcript info if available
      });
      
      // If transcript is available, log it
      if (transcriptData) {
        logger.log('✅ Transcript extracted:', transcriptData.text.substring(0, 100) + '...', `(${transcriptData.language}, ${transcriptData.is_generated ? 'Generated' : 'Original'})`);
      }
      
      // Clear any previously generated content
      setGeneratedContent('');
    } catch (error) {
      logger.error('❌ Error extracting YouTube info:', error);
      setYoutubeVideoInfo(null);
      setGeneratedContent('');
    } finally {
      setExtractingVideoInfo(false);
    }
  }, [featureType]);

  // Generate content from description
  const generateContentFromDescription = useCallback(async (description, promptText) => {
    if (!description || !description.trim()) {
      return;
    }

    setGeneratingContent(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_BASE}/${featureType}/generate`, {
        description: description,
        prompt: promptText || null,
      }, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        timeout: 60000, // 60 seconds timeout for AI generation
      });

      setGeneratedContent(response.data[resultField] || response.data.content || '');
    } catch (error) {
      logger.error(`Error generating ${featureType}:`, error);
      setGeneratedContent('');
      // Don't show error to user, just silently fail
    } finally {
      setGeneratingContent(false);
    }
  }, [featureType, resultField]);

  // Fetch prompt
  const fetchPrompt = useCallback(async () => {
    setPromptLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API_BASE}/${featureType}/prompt`, {
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
      });
      
      if (response.data) {
        // The API returns 'prompt' not 'prompt_text'
        const promptText = response.data.prompt || response.data.prompt_text || '';
        setPrompt(promptText);
        setPromptVersion(response.data.version || 1);
        setPromptTotalVersions(response.data.total_versions || 1);
        setSelectedPromptId(response.data.id || null);
        setSelectedPromptVersionForUse(response.data.version || 1);
        logger.log(`✅ ${featureType} prompt loaded:`, response.data);
      }
    } catch (error) {
      logger.error(`❌ Error fetching ${featureType} prompt:`, error);
      setPrompt('');
    } finally {
      setPromptLoading(false);
    }
  }, [featureType]);

  // Fetch specific prompt version
  const fetchPromptVersion = useCallback(async (version) => {
    setPromptLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/${featureType}/prompt/version/${version}`, {
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
      });

      if (response.data) {
        // The API may return 'prompt' or 'prompt_text'
        const promptText = response.data.prompt || response.data.prompt_text || '';
        logger.log(`📝 Loading version ${version}, prompt text length:`, promptText.length);
        logger.log(`📝 Full response data:`, response.data);
        setPrompt(promptText);
        setEditedPrompt(promptText); // Update edited prompt too
        setIsEditingPrompt(false); // Exit editing mode when navigating versions
        setPromptVersion(response.data.version || version);
        setSelectedPromptId(response.data.id || null);
        logger.log(`✅ ${featureType} prompt version ${version} loaded:`, response.data);
      } else {
        logger.error(`⚠️ No data in response for version ${version}`);
      }
    } catch (error) {
      logger.error(`❌ Error fetching ${featureType} prompt version ${version}:`, error);
    } finally {
      setPromptLoading(false);
    }
  }, [featureType]);

  // Handle prompt version navigation
  const handlePromptVersionChange = useCallback((direction) => {
    const newVersion = direction === 'next' 
      ? Math.min(promptVersion + 1, promptTotalVersions)
      : Math.max(promptVersion - 1, 1);
    
    if (newVersion !== promptVersion) {
      fetchPromptVersion(newVersion);
    }
  }, [promptVersion, promptTotalVersions, fetchPromptVersion]);

  // Clear state
  const clearState = useCallback(() => {
    setYoutubeUrl('');
    setYoutubeVideoInfo(null);
    setGeneratedContent('');
    setCurrentProjectId(null);
  }, []);

  // Save prompt
  const savePrompt = useCallback(async () => {
    if (!editedPrompt || !editedPrompt.trim()) {
      alert('Prompt cannot be empty');
      return;
    }

    setPromptLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_BASE}/${featureType}/prompt`, {
        prompt: editedPrompt.trim()
      }, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
      });

      if (response.data) {
        const promptText = response.data.prompt || response.data.prompt_text || '';
        setPrompt(promptText);
        setPromptVersion(response.data.version || 1);
        setPromptTotalVersions(response.data.total_versions || 1);
        setSelectedPromptId(response.data.id || null);
        setSelectedPromptVersionForUse(response.data.version || 1);
        setIsEditingPrompt(false);
        setEditedPrompt(promptText);
      }
    } catch (error) {
      logger.error(`❌ Error saving ${featureType} prompt:`, error);
      alert(`Failed to save prompt: ${error.response?.data?.detail || error.message}`);
    } finally {
      setPromptLoading(false);
    }
  }, [editedPrompt, featureType]);

  return {
    // State
    youtubeUrl,
    youtubeVideoInfo,
    extractingVideoInfo,
    generatedContent,
    currentProjectId,
    generatingContent,
    prompt,
    promptVersion,
    promptTotalVersions,
    selectedPromptId,
    selectedPromptVersionForUse,
    isEditingPrompt,
    editedPrompt,
    promptLoading,
    
    // Setters
    setYoutubeUrl,
    setYoutubeVideoInfo,
    setExtractingVideoInfo,
    setGeneratedContent,
    setCurrentProjectId,
    setGeneratingContent,
    setPrompt,
    setPromptVersion,
    setPromptTotalVersions,
    setSelectedPromptId,
    setSelectedPromptVersionForUse,
    setIsEditingPrompt,
    setEditedPrompt,
    setPromptLoading,
    
    // Functions
    extractYoutubeVideoInfo,
    generateContentFromDescription,
    fetchPrompt,
    fetchPromptVersion,
    handlePromptVersionChange,
    clearState,
    savePrompt,
  };
};
